/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import Layout from '../../bootstrap/Layout';
import Container from '../../bootstrap/Container';
import Spinner from '../../bootstrap/Spinner';
import {
  setPendingApprovals,
  setApprovalLoading,
  setApprovalError,
  setCurrentReview,
  approveBookingSuccess,
  rejectBookingSuccess,
  clearCurrentReview,
} from '../../redux/action/approvalAction';
import {
  getCurrentUser,
  getPendingApprovals,
  reviewBooking,
  approveBooking,
  rejectBooking,
} from '../../helpers/approvalAPI';
import { setUserInfo } from '../../redux/action/approvalAction';
import {
  DashboardWrapper,
  DashboardHeader,
  Title,
  Subtitle,
  StatsRow,
  StatCard,
  StatLabel,
  StatValue,
  TableContainer,
  TableHeader,
  TableTitle,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableCell,
  StatusBadge,
  RiskBadge,
  ActionButtons,
  Button,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  LoadingContainer,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CancelButton,
  SubmitButton,
  FormGroup,
  Label,
  TextArea,
  InfoRow,
  InfoLabel,
  InfoValue,
  RiskFactorList,
  RiskFactorItem,
  RiskScore,
  PriceComparison,
  PriceItem,
  PriceLabel,
  PriceValue,
  Divider,
} from './ApprovalDashboard.styles';

const ApprovalDashboard = () => {
  const dispatch = useDispatch();
  const { pendingApprovals, currentReview, loading, error, userRole, canApprove } = useSelector(
    (state) => state.approval
  );

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    // Check if auth_token is in URL (from backend redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const authToken = urlParams.get('auth_token');

    if (authToken) {
      try {
        // Decode base64 token
        const userData = JSON.parse(atob(authToken));

        // Determine approval stage based on role
        let approvalStage = null;
        let canApprove = false;
        const roleLower = userData.role?.toLowerCase() || '';

        // Only certain roles can access Approval Dashboard
        if (roleLower.includes('supervisor')) {
          approvalStage = 1;
          canApprove = true;
        } else if (roleLower.includes('management') || roleLower.includes('manager')) {
          approvalStage = 2;
          canApprove = true;
        } else if (roleLower.includes('director')) {
          approvalStage = 3;
          canApprove = true;
        } else if (roleLower.includes('admin')) {
          approvalStage = 3; // Admin can approve all stages
          canApprove = true;
        }
        // Kasir, CS, Driver, etc. cannot access - canApprove stays false

        // Set user info in Redux
        dispatch(setUserInfo({
          ...userData,
          approval_stage: approvalStage,
          can_approve: canApprove,
        }));

        // Clean URL (remove token)
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (err) {
        console.error('Failed to decode auth token:', err);
        fetchCurrentUser();
      }
    } else {
      fetchCurrentUser();
    }

    fetchPendingApprovals();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await getCurrentUser();
      if (response.status === 'success') {
        if (response.data) {
          // Calculate can_approve based on role (same logic as auth_token)
          let canApprove = response.data.can_approve || false;
          const roleLower = response.data.role?.toLowerCase() || '';

          // Double-check role-based access
          if (roleLower.includes('supervisor') ||
            roleLower.includes('management') ||
            roleLower.includes('manager') ||
            roleLower.includes('director') ||
            roleLower.includes('admin')) {
            canApprove = true;
          }

          dispatch(setUserInfo({
            ...response.data,
            can_approve: canApprove,
          }));
        } else {
          // User not authenticated - set can_approve to false
          dispatch(setUserInfo({
            role: 'Unknown',
            can_approve: false,
          }));
          toast.warn('Please login to access approval dashboard');
          console.log('Not authenticated:', response.message);
        }
      }
    } catch (err) {
      console.error('Failed to fetch user info:', err);
      // Set can_approve to false on error
      dispatch(setUserInfo({
        role: 'Unknown',
        can_approve: false,
      }));
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      dispatch(setApprovalLoading(true));
      const response = await getPendingApprovals();

      if (response.status === 'success') {
        dispatch(setPendingApprovals(response));
      } else {
        throw new Error(response.message || 'Gagal mengambil data');
      }
    } catch (err) {
      dispatch(setApprovalError(err.message || 'Gagal mengambil data approval'));
      toast.error(err.message || 'Gagal mengambil data approval');
    }
  };

  const handleReview = async (approval) => {
    try {
      setActionLoading(true);
      const response = await reviewBooking(approval.booking_id);

      if (response.status === 'success') {
        dispatch(setCurrentReview(response.data));
        setSelectedApproval(approval);
        setShowReviewModal(true);
      } else {
        throw new Error(response.message || 'Gagal review booking');
      }
    } catch (err) {
      toast.error(err.message || 'Gagal review booking');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Yakin ingin approve booking ini?')) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await approveBooking(selectedApproval.id);

      if (response.status === 'success') {
        dispatch(approveBookingSuccess({ approval_id: selectedApproval.id }));
        toast.success('Booking berhasil diapprove!');
        setShowReviewModal(false);
        setSelectedApproval(null);
        dispatch(clearCurrentReview());
      } else {
        throw new Error(response.message || 'Gagal approve');
      }
    } catch (err) {
      toast.error(err.message || 'Gagal approve booking');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = () => {
    setShowReviewModal(false);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Alasan penolakan harus diisi');
      return;
    }

    try {
      setActionLoading(true);
      const response = await rejectBooking(selectedApproval.id, rejectionReason);

      if (response.status === 'success') {
        dispatch(rejectBookingSuccess({ approval_id: selectedApproval.id }));
        toast.success('Booking berhasil ditolak');
        setShowRejectModal(false);
        setSelectedApproval(null);
        setRejectionReason('');
        dispatch(clearCurrentReview());
      } else {
        throw new Error(response.message || 'Gagal reject');
      }
    } catch (err) {
      toast.error(err.message || 'Gagal reject booking');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseReviewModal = () => {
    if (!actionLoading) {
      setShowReviewModal(false);
      setSelectedApproval(null);
      dispatch(clearCurrentReview());
    }
  };

  const handleCloseRejectModal = () => {
    if (!actionLoading) {
      setShowRejectModal(false);
      setRejectionReason('');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'low':
        return '#27ae60';
      case 'medium':
        return '#f39c12';
      case 'high':
        return '#e74c3c';
      case 'critical':
        return '#c0392b';
      default:
        return '#95a5a6';
    }
  };

  const getRiskLabel = (riskLevel) => {
    switch (riskLevel) {
      case 'low':
        return 'Low Risk';
      case 'medium':
        return 'Medium Risk';
      case 'high':
        return 'High Risk';
      case 'critical':
        return 'Critical Risk';
      default:
        return riskLevel;
    }
  };

  const totalPending = pendingApprovals.length;
  const totalAmount = pendingApprovals.reduce(
    (sum, approval) => sum + parseFloat(approval.contract_price || 0),
    0
  );

  // Check if user has permission to access this dashboard
  if (canApprove === false) {
    return (
      <Layout>
        <Container>
          <DashboardWrapper>
            <DashboardHeader>
              <div>
                <Title>Approval Dashboard</Title>
                <Subtitle style={{ color: '#e74c3c' }}>
                  Akses Ditolak
                </Subtitle>
              </div>
            </DashboardHeader>
            <EmptyState>
              <EmptyStateIcon style={{ color: '#e74c3c' }}>⛔</EmptyStateIcon>
              <EmptyStateText>
                Anda tidak memiliki izin untuk mengakses Approval Dashboard.
                <br />
                <small style={{ color: '#7f8c8d' }}>
                  Role Anda: <strong>{userRole || 'Unknown'}</strong>
                  <br />
                  Hanya Supervisor, Manager, Director, dan Admin yang dapat mengakses halaman ini.
                </small>
              </EmptyStateText>
              <Button
                color="#6c757d"
                onClick={() => window.location.href = '/modules/backend/booking/dashboard'}
                style={{ marginTop: '20px' }}
              >
                ← Kembali ke Dashboard
              </Button>
            </EmptyState>
          </DashboardWrapper>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <DashboardWrapper>
          <DashboardHeader>
            <div>
              <Title>Approval Dashboard</Title>
              <Subtitle>
                Manage booking approvals - Role: <strong>{userRole || 'Unknown'}</strong>
              </Subtitle>
            </div>
            <Button
              color="#6c757d"
              onClick={() => window.location.href = '/bus365/dashboard'}
              style={{ marginLeft: 'auto' }}
            >
              ← Kembali ke Dashboard
            </Button>
          </DashboardHeader>

          <StatsRow>
            <StatCard color="#3498db">
              <StatLabel>Pending Approvals</StatLabel>
              <StatValue>{totalPending}</StatValue>
            </StatCard>
            <StatCard color="#9b59b6">
              <StatLabel>Total Amount</StatLabel>
              <StatValue>{formatCurrency(totalAmount)}</StatValue>
            </StatCard>
          </StatsRow>

          <TableContainer>
            <TableHeader>
              <TableTitle>Pending Approvals</TableTitle>
            </TableHeader>

            {loading && !pendingApprovals.length ? (
              <LoadingContainer>
                <Spinner />
              </LoadingContainer>
            ) : pendingApprovals.length === 0 ? (
              <EmptyState>
                <EmptyStateIcon>✓</EmptyStateIcon>
                <EmptyStateText>
                  Tidak ada approval pending. Semua booking sudah diproses!
                </EmptyStateText>
              </EmptyState>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Booking Number</TableHeaderCell>
                    <TableHeaderCell>Customer</TableHeaderCell>
                    <TableHeaderCell>Departure Date</TableHeaderCell>
                    <TableHeaderCell>Contract Price</TableHeaderCell>
                    <TableHeaderCell>Risk Level</TableHeaderCell>
                    <TableHeaderCell>Stage</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <tbody>
                  {pendingApprovals.map((approval) => (
                    <TableRow key={approval.id}>
                      <TableCell>
                        <strong>{approval.booking_number}</strong>
                      </TableCell>
                      <TableCell>{approval.customer_name}</TableCell>
                      <TableCell>
                        {new Date(approval.departure_date).toLocaleDateString('id-ID')}
                      </TableCell>
                      <TableCell>
                        <strong>{formatCurrency(approval.contract_price)}</strong>
                      </TableCell>
                      <TableCell>
                        <RiskBadge color={getRiskColor(approval.risk_level)}>
                          {getRiskLabel(approval.risk_level)}
                        </RiskBadge>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status="pending">{approval.approval_stage}</StatusBadge>
                      </TableCell>
                      <TableCell>
                        <ActionButtons>
                          <Button
                            color="#3498db"
                            onClick={() => handleReview(approval)}
                            disabled={actionLoading}
                          >
                            Review
                          </Button>
                        </ActionButtons>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            )}
          </TableContainer>

          {/* Review Modal */}
          {showReviewModal && currentReview && (
            <Modal onClick={handleCloseReviewModal}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                  <ModalTitle>Review Booking - {currentReview.booking?.booking_number}</ModalTitle>
                </ModalHeader>
                <ModalBody>
                  <FormGroup>
                    <Label>Customer Information</Label>
                    <InfoRow>
                      <InfoLabel>Name:</InfoLabel>
                      <InfoValue>{currentReview.booking?.customer_name}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Phone:</InfoLabel>
                      <InfoValue>{currentReview.booking?.customer_phone}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Departure:</InfoLabel>
                      <InfoValue>
                        {new Date(currentReview.booking?.departure_date).toLocaleDateString('id-ID')}
                      </InfoValue>
                    </InfoRow>
                  </FormGroup>

                  <Divider />

                  <FormGroup>
                    <Label>Price Comparison</Label>
                    <PriceComparison>
                      <PriceItem>
                        <PriceLabel>Standard Price:</PriceLabel>
                        <PriceValue>
                          {currentReview.standard_price
                            ? formatCurrency(currentReview.standard_price)
                            : 'N/A'}
                        </PriceValue>
                      </PriceItem>
                      <PriceItem>
                        <PriceLabel>Contract Price:</PriceLabel>
                        <PriceValue>
                          {formatCurrency(currentReview.booking?.contract_price)}
                        </PriceValue>
                      </PriceItem>
                      {currentReview.standard_price && (
                        <PriceItem>
                          <PriceLabel>Discount:</PriceLabel>
                          <PriceValue style={{ color: '#e74c3c' }}>
                            {(
                              ((currentReview.standard_price -
                                currentReview.booking?.contract_price) /
                                currentReview.standard_price) *
                              100
                            ).toFixed(2)}
                            %
                          </PriceValue>
                        </PriceItem>
                      )}
                    </PriceComparison>
                  </FormGroup>

                  <Divider />

                  <FormGroup>
                    <Label>Risk Evaluation</Label>
                    <InfoRow>
                      <InfoLabel>Risk Level:</InfoLabel>
                      <RiskBadge
                        color={getRiskColor(currentReview.risk_evaluation?.risk_level)}
                      >
                        {getRiskLabel(currentReview.risk_evaluation?.risk_level)}
                      </RiskBadge>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Risk Score:</InfoLabel>
                      <RiskScore>{currentReview.risk_evaluation?.risk_score} / 100</RiskScore>
                    </InfoRow>
                  </FormGroup>

                  {currentReview.risk_evaluation?.factors &&
                    currentReview.risk_evaluation.factors.length > 0 && (
                      <FormGroup>
                        <Label>Risk Factors:</Label>
                        <RiskFactorList>
                          {currentReview.risk_evaluation.factors.map((factor, index) => (
                            <RiskFactorItem key={index}>
                              {factor.description} (+{factor.points} points)
                            </RiskFactorItem>
                          ))}
                        </RiskFactorList>
                      </FormGroup>
                    )}

                  {currentReview.price_override && (
                    <>
                      <Divider />
                      <FormGroup>
                        <Label>Price Override Justification</Label>
                        <InfoValue>{currentReview.price_override.justification}</InfoValue>
                      </FormGroup>
                    </>
                  )}
                </ModalBody>
                <ModalFooter>
                  <CancelButton onClick={handleRejectClick} disabled={actionLoading}>
                    Reject
                  </CancelButton>
                  <SubmitButton onClick={handleApprove} disabled={actionLoading}>
                    {actionLoading ? 'Processing...' : 'Approve'}
                  </SubmitButton>
                </ModalFooter>
              </ModalContent>
            </Modal>
          )}

          {/* Reject Modal */}
          {showRejectModal && (
            <Modal onClick={handleCloseRejectModal}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                  <ModalTitle>Reject Booking</ModalTitle>
                </ModalHeader>
                <ModalBody>
                  <FormGroup>
                    <Label htmlFor="rejection_reason">Alasan Penolakan *</Label>
                    <TextArea
                      id="rejection_reason"
                      rows="4"
                      placeholder="Jelaskan alasan penolakan booking ini..."
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      disabled={actionLoading}
                    />
                  </FormGroup>
                </ModalBody>
                <ModalFooter>
                  <CancelButton onClick={handleCloseRejectModal} disabled={actionLoading}>
                    Batal
                  </CancelButton>
                  <SubmitButton
                    onClick={handleRejectSubmit}
                    disabled={actionLoading}
                    style={{ background: '#e74c3c' }}
                  >
                    {actionLoading ? 'Processing...' : 'Reject Booking'}
                  </SubmitButton>
                </ModalFooter>
              </ModalContent>
            </Modal>
          )}
        </DashboardWrapper>
      </Container>
    </Layout>
  );
};

export default ApprovalDashboard;
